/**
 * Browser-compatible Zcash lightwalletd client.
 *
 * Compact blocks are fetched via a serverless proxy (/api/compact-blocks)
 * that bridges browser HTTP to lightwalletd gRPC. UTXOs and broadcast
 * still use Tatum/Blockchair REST APIs.
 */

const TATUM_BASE = "https://zcash-mainnet.gateway.tatum.io";
const BLOCKCHAIR_BASE = "https://api.blockchair.com/zcash";
const DEFAULT_POLL_INTERVAL_MS = 10_000;
const DEFAULT_POLL_TIMEOUT_MS = 600_000; // 10 min
const MAX_BLOCKS_PER_REQUEST = 1000;

export interface UTXO {
  txid: string;
  vout: number;
  amount: string;
  script: string;
  height: number;
}

export interface LightwalletClient {
  getUTXOs(tAddr: string): Promise<UTXO[]>;
  getTxBlockHeight(txid: string): Promise<number | null>;
  getAddressShieldBlockHeight(tAddr: string, knownTxid?: string): Promise<number | null>;
  getCompactBlocks(startHeight: number, endHeight: number): Promise<Uint8Array>;
  getLatestBlockHeight(): Promise<number>;
  getOrchardTreeState(height: number): Promise<string>;
  sendRawTransaction(rawTxHex: string): Promise<string>;
  pollForDeposit(
    tAddr: string,
    opts?: {
      intervalMs?: number;
      timeoutMs?: number;
      excludeUtxos?: Array<{ txid: string; vout: number }>;
    },
  ): Promise<UTXO[]>;
}

export interface LightwalletClientOptions {
  tatumApiKey?: string;
}

/**
 * Create a lightwalletd client backed by our serverless proxy (for compact
 * blocks and block height) and Tatum/Blockchair (for UTXOs and broadcast).
 */
export function createLightwalletClient(
  opts: LightwalletClientOptions = {},
): LightwalletClient {
  const tatumHeaders: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (opts.tatumApiKey) {
    tatumHeaders["x-api-key"] = opts.tatumApiKey;
  }

  async function tatumRpc<T>(method: string, params: unknown[] = []): Promise<T> {
    const resp = await fetch(TATUM_BASE, {
      method: "POST",
      headers: tatumHeaders,
      body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }),
    });
    if (!resp.ok) {
      throw new Error(`Tatum RPC ${method} failed: ${resp.status}`);
    }
    const json = (await resp.json()) as { result?: T; error?: { message: string } };
    if (json.error) {
      throw new Error(`Tatum RPC ${method}: ${json.error.message}`);
    }
    return json.result as T;
  }

  return {
    async getTxBlockHeight(txid: string): Promise<number | null> {
      try {
        const tx = await tatumRpc<{ height?: number; blockheight?: number }>(
          "getrawtransaction",
          [txid, 1],
        );
        const h = tx?.height ?? tx?.blockheight;
        return h && h > 0 ? h : null;
      } catch {
        return null;
      }
    },

    async getAddressShieldBlockHeight(tAddr: string, knownTxid?: string): Promise<number | null> {
      // If we already know the shield txid, look it up directly — one request.
      if (knownTxid) {
        // Try Blockchair first
        const heightResp = await fetch(`/api/tx-heights?txids=${knownTxid}`);
        if (heightResp.ok) {
          const heightData = (await heightResp.json()) as {
            data?: Record<string, { transaction?: { block_id?: number } }>;
          };
          const h = heightData.data?.[knownTxid]?.transaction?.block_id;
          if (h && h > 0) return h;
        }
        // Fallback: Tatum getrawtransaction
        return await this.getTxBlockHeight(knownTxid);
      }

      // Fallback: get all txids for the address, then batch-lookup heights in
      // chunks of 10 (Blockchair batch limit) and return the minimum.
      const addrResp = await fetch(`/api/utxos/${tAddr}`);
      if (!addrResp.ok) return null;
      const addrData = (await addrResp.json()) as {
        data?: Record<string, { transactions?: string[] }>;
      };
      const txids = addrData.data?.[tAddr]?.transactions ?? [];
      if (txids.length === 0) return null;

      const CHUNK = 10;
      let minHeight: number | null = null;
      for (let i = 0; i < txids.length; i += CHUNK) {
        const chunk = txids.slice(i, i + CHUNK);
        const heightResp = await fetch(`/api/tx-heights?txids=${chunk.join(",")}`);
        if (!heightResp.ok) continue;
        const heightData = (await heightResp.json()) as {
          data?: Record<string, { transaction?: { block_id?: number } }>;
        };
        for (const txid of chunk) {
          const h = heightData.data?.[txid]?.transaction?.block_id;
          if (h && h > 0 && (minHeight === null || h < minHeight)) {
            minHeight = h;
          }
        }
      }
      return minHeight;
    },

    async getUTXOs(tAddr: string): Promise<UTXO[]> {
      const resp = await fetch(`/api/utxos/${tAddr}`);
      if (!resp.ok) {
        throw new Error(`UTXO lookup failed: ${resp.status}`);
      }
      const data = (await resp.json()) as {
        data?: Record<string, {
          utxo?: Array<{
            transaction_hash: string;
            index: number;
            value: number;
            block_id: number;
            script_hex?: string;
          }>;
        }>;
      };

      const addrData = data.data?.[tAddr];
      if (!addrData?.utxo?.length) return [];

      // Only return confirmed UTXOs (block_id > 0).
      // Blockchair includes mempool entries with block_id = -1; spending those
      // causes "Missing inputs" on Tatum's node because they aren't in its UTXO set yet.
      return addrData.utxo
        .filter((u) => u.block_id > 0)
        .map((u) => ({
          txid: u.transaction_hash,
          vout: u.index,
          amount: (u.value / 1e8).toFixed(8),
          script: u.script_hex ?? "",
          height: u.block_id,
        }));
    },

    async getCompactBlocks(
      startHeight: number,
      endHeight: number,
    ): Promise<Uint8Array> {
      const chunks: Uint8Array[] = [];
      for (let start = startHeight; start <= endHeight; start += MAX_BLOCKS_PER_REQUEST) {
        const end = Math.min(start + MAX_BLOCKS_PER_REQUEST - 1, endHeight);
        const resp = await fetch("/api/compact-blocks", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ startHeight: start, endHeight: end }),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }));
          throw new Error(`compact-blocks failed: ${(err as any).error ?? resp.status}`);
        }
        const buf = new Uint8Array(await resp.arrayBuffer());
        chunks.push(buf);
      }

      if (chunks.length === 1) return chunks[0]!;
      const totalLen = chunks.reduce((s, c) => s + c.length, 0);
      const merged = new Uint8Array(totalLen);
      let offset = 0;
      for (const chunk of chunks) {
        merged.set(chunk, offset);
        offset += chunk.length;
      }
      return merged;
    },

    async getLatestBlockHeight(): Promise<number> {
      const resp = await fetch("/api/latest-block");
      if (!resp.ok) {
        throw new Error(`latest-block failed: ${resp.status}`);
      }
      const data = (await resp.json()) as { height: number };
      return data.height;
    },

    async getOrchardTreeState(height: number): Promise<string> {
      const resp = await fetch("/api/tree-state", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ height }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }));
        throw new Error(`tree-state failed: ${(err as any).error ?? resp.status}`);
      }
      const data = (await resp.json()) as { orchardTree: string };
      return data.orchardTree;
    },

    async sendRawTransaction(rawTxHex: string): Promise<string> {
      try {
        return await tatumRpc<string>("sendrawtransaction", [rawTxHex]);
      } catch (primaryErr) {
        const msg = primaryErr instanceof Error ? primaryErr.message : String(primaryErr);

        // "Already in mempool/blockchain" means the tx was accepted previously
        if (
          msg.includes("already in block chain") ||
          msg.includes("already in the mempool") ||
          msg.includes("txn-already-known") ||
          msg.includes("txn-already-in-mempool") ||
          msg.includes("txn-mempool-conflict")
        ) {
          console.log(`[zcash] tx already in flight (${msg}), treating as success`);
          return computeTxid(rawTxHex);
        }

        // Only fall through to Blockchair for network/HTTP errors, not semantic rejections
        if (!isNetworkError(msg)) {
          throw primaryErr;
        }

        console.log(`[zcash] Tatum broadcast failed (${msg}), trying Blockchair fallback`);
        const resp = await fetch(`${BLOCKCHAIR_BASE}/push/transaction`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ data: rawTxHex }),
        });
        if (!resp.ok) {
          throw new Error(`Broadcast failed on both providers (Tatum: ${msg}, Blockchair: ${resp.status})`);
        }
        const result = (await resp.json()) as {
          data?: { transaction_hash?: string };
        };
        const hash = result.data?.transaction_hash;
        if (!hash) {
          throw new Error(`Broadcast may have succeeded but no txid returned (Tatum: ${msg})`);
        }
        return hash;
      }
    },

    async pollForDeposit(
      tAddr: string,
      opts = {} as {
        intervalMs?: number;
        timeoutMs?: number;
        excludeUtxos?: Array<{ txid: string; vout: number }>;
      },
    ): Promise<UTXO[]> {
      const interval = opts.intervalMs ?? DEFAULT_POLL_INTERVAL_MS;
      const timeout = opts.timeoutMs ?? DEFAULT_POLL_TIMEOUT_MS;
      const deadline = Date.now() + timeout;

      const excludeSet = new Set(
        (opts.excludeUtxos ?? []).map((u) => `${u.txid}:${u.vout}`),
      );

      while (Date.now() < deadline) {
        const allUtxos = await this.getUTXOs(tAddr);
        const newUtxos = excludeSet.size > 0
          ? allUtxos.filter((u) => !excludeSet.has(`${u.txid}:${u.vout}`))
          : allUtxos;
        if (newUtxos.length > 0) return newUtxos;
        await new Promise((r) => setTimeout(r, interval));
      }

      throw new Error(`No deposit detected at ${tAddr} within ${timeout}ms`);
    },
  };
}

function isNetworkError(msg: string): boolean {
  return (
    msg.includes("failed:") ||
    msg.includes("Failed to fetch") ||
    msg.includes("NetworkError") ||
    msg.includes("AbortError") ||
    msg.includes("ECONNREFUSED") ||
    msg.includes("ETIMEDOUT") ||
    /Tatum RPC .* failed: \d+/.test(msg)
  );
}

/**
 * Compute the txid from a raw Zcash v5 transaction hex.
 * txid = SHA-256d of the raw bytes, reversed to little-endian display order.
 */
function computeTxid(rawTxHex: string): string {
  // Zcash txids are double-SHA256 of the raw tx, byte-reversed.
  // In the browser we use SubtleCrypto; fall back to a placeholder
  // if unavailable (should never happen in modern browsers).
  // This is a synchronous best-effort: we return the hex prefix as
  // an identifiable marker since the full hash needs async crypto.
  // The caller already has the txid from the Lit Action; this path
  // only fires for "already in mempool" where we lack it.
  return `mempool:${rawTxHex.slice(0, 16)}`;
}
